<template>
  <b-card no-body>

    <!-- ── Toolbar ── -->
    <div class="m-2">
      <b-row>
        <b-col cols="12" md="9" class="d-flex align-items-center flex-wrap mb-1 mb-md-0" style="gap:0.5rem">
          <v-select
            v-model="statusFilter"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="actions_a"
            label="name"
            class="invoice-filter-select"
            placeholder="Filter by Action"
          />
          <b-button
            variant="primary"
            :to="{ name: 'property-owner-create', params: { ownerId: ownerId } }"
          >
            <feather-icon icon="PlusIcon" class="mr-50" size="14" />
            Add Property
          </b-button>
          <b-form-checkbox v-model="statusId" value="1" class="custom-control-primary">
            Available Only
          </b-form-checkbox>
        </b-col>

        <b-col cols="12" md="3" class="d-flex align-items-center justify-content-end">
          <b-form-input
            v-model="searchQuery"
            placeholder="Search..."
            style="max-width:200px"
          />
        </b-col>
      </b-row>
    </div>

    <!-- ── Table ── -->
    <b-table
      ref="refPropertyListTable"
      :items="fetchProperties"
      responsive
      striped
      :fields="tableColumns_property"
      primary-key="id"
      :sort-by.sync="sortBy"
      show-empty
      empty-text="No matching records found"
      :sort-desc.sync="isSortDirDesc"
      class="position-relative"
    >

      <!-- Image -->
      <template #cell(image)="data">
        <b-link :to="{ name: 'property-upload', params: { id: data.item.id } }">
          <b-img
            :src="data.item.property_images[0] ? data.item.property_images[0].fullfilepath : defaultPropertyImg"
            class="img_property"
            fluid
          />
        </b-link>
      </template>

      <!-- Unit -->
      <template #cell(unit)="data">
        {{ data.item.units }} {{ data.item.address_no }}
      </template>

      <!-- Property -->
      <template #cell(property)="data">
        <div class="list-property">
          <div class="d-flex align-items-center flex-wrap mb-25" style="gap:0.25rem">
            <router-link :to="{ name: 'property-edit', params: { id: data.item.id } }"
              :class="{ 'text-red': data.item.is_active == 0 }">
              {{ data.item.property_code }}
            </router-link>
            <b-badge :variant="actionVariant(data.item.action_id)">
              {{ data.item.action_name }}
            </b-badge>
            <b-badge :variant="data.item.status_id === 1 ? 'light-success' : 'light-secondary'">
              {{ data.item.status_code }}
            </b-badge>
            <b-badge variant="light-primary" class="p-0">
              <b-dropdown no-caret right variant="link" toggle-class="d-flex align-items-center px-50 py-25">
                <template #button-content>
                  <feather-icon icon="Share2Icon" size="14" />
                </template>
                <b-dropdown-item :href="condoLink(data.item)" target="_blank">Open Link</b-dropdown-item>
                <b-dropdown-item @click="copyLink(data.item)">Copy Link</b-dropdown-item>
                <b-dropdown-item @click="doCopy(data.item)">Copy with Details</b-dropdown-item>
                <b-dropdown-item @click="copyLinkNetwork(data.item)">Share to Network</b-dropdown-item>
              </b-dropdown>
            </b-badge>
          </div>
          <div class="text-muted small">{{ data.item.place_name[currentLocale] }}</div>
          <p v-if="data.item.comments[0]" class="text-truncate text-warning small mb-0 width-200">
            {{ filterTags(data.item.comments[0].comment) }}
          </p>
          <b-badge variant="light-secondary" class="cursor-pointer mt-25" @click="formcomment_show(data.item.id)">
            Comment
          </b-badge>
        </div>
      </template>

      <!-- Prices -->
      <template #cell(rent_price)="data">
        {{ formatComma(data.item.rent_price) }}
      </template>
      <template #cell(sale_price)="data">
        {{ formatComma(data.item.sale_price) }}
      </template>

      <!-- Owner (expandable) -->
      <template #cell(owner)="row">
        <b-button
          variant="flat-primary"
          size="sm"
          class="p-0"
          @click="row.toggleDetails"
        >
          <feather-icon :icon="row.detailsShowing ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" class="mr-25" />
          {{ row.item.owner_firstname }} {{ row.item.owner_lastname }}
        </b-button>
      </template>

      <!-- Row details -->
      <template #row-details="row">
        <b-card class="border">
          <b-row>
            <!-- Edit fields -->
            <b-col md="6" class="mb-1">
              <b-row>
                <b-col md="6" class="mb-1">
                  <label class="small font-weight-bold">Available Date</label>
                  <b-form-datepicker v-model="row.item.availble_date" reset-button close-button />
                </b-col>
                <b-col md="6" class="mb-1">
                  <label class="small font-weight-bold">Expire Date</label>
                  <b-form-datepicker v-model="row.item.expire_date" reset-button close-button />
                </b-col>
                <b-col md="6" class="mb-1">
                  <label class="small font-weight-bold">Sale Price</label>
                  <b-form-input v-model="row.item.sale_price" />
                </b-col>
                <b-col md="6" class="mb-1">
                  <label class="small font-weight-bold">Rent Price</label>
                  <b-form-input v-model="row.item.rent_price" />
                </b-col>
                <b-col md="12" class="mb-1">
                  <label class="small font-weight-bold">Status</label>
                  <v-select
                    v-model="row.item.status_id"
                    :reduce="option => option.id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    label="name"
                    :options="statuses"
                  />
                </b-col>
                <b-col md="12">
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="updateDate(row.item.id, row.item.availble_date, row.item.status_id, row.item.sale_price, row.item.rent_price, row.item.sqm, row.item.expire_date)"
                  >
                    <feather-icon icon="SaveIcon" class="mr-50" size="13" />
                    Save
                  </b-button>
                </b-col>
              </b-row>
            </b-col>

            <!-- Owner info -->
            <b-col md="3" class="mb-1">
              <p class="mb-50">
                <strong>Name: </strong>
                <router-link :to="{ name: 'clients-edit', params: { id: row.item.owner_id } }" target="_blank">
                  {{ row.item.owner_firstname }} {{ row.item.owner_lastname }}
                </router-link>
              </p>
              <p class="mb-0"><strong>Email:</strong></p>
              <template v-if="row.item.emails">
                <div v-for="oemail in row.item.emails" :key="'email-'+oemail.id">
                  <a :href="'mailto:'+oemail.email" class="small">{{ oemail.email }}</a>
                </div>
              </template>
            </b-col>

            <!-- Phone info -->
            <b-col md="3" class="mb-1">
              <p class="mb-0"><strong>Phone:</strong></p>
              <template v-if="row.item.phones">
                <div v-for="ophone in row.item.phones" :key="'phone-'+ophone.id">
                  <a :href="'tel:'+ophone.phone_no" class="small">{{ ophone.phone_no }}</a>
                </div>
              </template>
            </b-col>
          </b-row>

          <b-button size="sm" variant="outline-secondary" @click="row.toggleDetails">
            <feather-icon icon="ChevronUpIcon" class="mr-25" size="13" />
            Hide
          </b-button>
        </b-card>
      </template>

      <!-- Actions -->
      <template #cell(actions)="data">
        <b-dropdown variant="link" toggle-class="text-decoration-none" no-caret>
          <template #button-content>
            <feather-icon icon="MoreVerticalIcon" size="16" class="text-body" />
          </template>
          <b-dropdown-item :to="{ name: 'property-edit', params: { id: data.item.id } }">
            <feather-icon icon="Edit2Icon" class="mr-50" />
            Edit
          </b-dropdown-item>
          <b-dropdown-item @click="deleteconfirm(data.item.id)">
            <feather-icon icon="TrashIcon" class="mr-50" />
            Delete
          </b-dropdown-item>
        </b-dropdown>
      </template>

    </b-table>

    <!-- ── Pagination ── -->
    <div class="mx-2 mb-2">
      <b-row>
        <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
          <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
        </b-col>
        <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalProperty"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text><feather-icon icon="ChevronLeftIcon" size="18" /></template>
            <template #next-text><feather-icon icon="ChevronRightIcon" size="18" /></template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>

    <!-- ── Comment Sidebar ── -->
    <b-sidebar
      id="sidebar-right"
      bg-variant="white"
      v-model="isSidebarcommentOpen"
      no-header
      right
      backdrop
      shadow
      ref="formOwner"
      class="side-bar-responsive"
    >
      <template>
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">Comment</h5>
          <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="formcomment_close" />
        </div>
        <comments :id="propety_id" v-if="isSidebarcommentOpen" v-on:childToClose="onSubmitClose" />
      </template>
    </b-sidebar>

  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BFormCheckbox, BButton, BTable, BLink,
  BBadge, BDropdown, BDropdownItem, BPagination, BImg, VBToggle, BSidebar, BFormDatepicker,
} from 'bootstrap-vue'
import { useClipboard } from '@vueuse/core'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { useToast } from 'vue-toastification/composition'
import vSelect from 'vue-select'
import { onUnmounted } from '@vue/composition-api'
import store from '@/store'
import usePropertyListOwner from './usePropertyListOwner'
import Comments from '../../components/comments/Comments.vue'
import propertyStoreModule from '../propertiesStoreModule'
import { filterTags } from '@core/utils/filter'
import axios from '@axios'
import router from '@/router'

const ACTION_VARIANTS = { 1: 'light-secondary', 2: 'light-info', 3: 'light-success', 4: 'light-warning' }
const DEFAULT_PROPERTY_IMG = "https://findbangkokroom.com/public/img/storage/1698858264911-picture.png"

export default {
  components: {
    BCard, BRow, BCol, BFormInput, BFormCheckbox, BButton, BTable, BLink,
    BBadge, BDropdown, BDropdownItem, BPagination, BImg, BSidebar, BFormDatepicker,
    Comments, vSelect, ToastificationContent,
  },

  directives: { Ripple, 'b-toggle': VBToggle },

  props: {
    presetId: {
      type: [String, Number],
      default: null,
    },
  },

  filters: {
    getcomma(text) {
      if (!text) return ''
      return String(text).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
  },

  data() {
    return {
      defaultPropertyImg: DEFAULT_PROPERTY_IMG,
      actions_a: [],
      ownerId: this.presetId || router.currentRoute.params.id,
      propety_id: '',
      isSidebarcommentOpen: false,
      userData: JSON.parse(localStorage.getItem('userData')),
      statuses: [
        { id: 1, name: 'Available now' },
        { id: 2, name: 'Rented' },
        { id: 3, name: 'Sale with tenant' },
        { id: 5, name: 'Own Stay' },
        { id: 6, name: 'Not Sure' },
        { id: 7, name: 'Cannot contact' },
        { id: 8, name: 'Follow up' },
        { id: 4, name: 'Sold' },
      ],
    }
  },

  setup(props) {
    const toast = useToast()
    const { copy } = useClipboard()

    const PROPERTY_APP_STORE_MODULE_NAME = 'app-property'
    if (!store.hasModule(PROPERTY_APP_STORE_MODULE_NAME)) store.registerModule(PROPERTY_APP_STORE_MODULE_NAME, propertyStoreModule)
    onUnmounted(() => {
      if (store.hasModule(PROPERTY_APP_STORE_MODULE_NAME)) store.unregisterModule(PROPERTY_APP_STORE_MODULE_NAME)
    })

    const {
      fetchProperties,
      tableColumns_property,
      perPage,
      currentPage,
      totalProperty,
      dataMeta,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refPropertyListTable,
      statusId,
      statusFilter,
      refetchData,
      deleteData,
      updateData,
    } = usePropertyListOwner(props.presetId)

    return {
      fetchProperties,
      tableColumns_property,
      perPage,
      currentPage,
      totalProperty,
      dataMeta,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refPropertyListTable,
      statusId,
      statusFilter,
      refetchData,
      deleteData,
      updateData,
      filterTags,
      copy,
      toast,
    }
  },

  computed: {
    currentLocale() {
      return this.$i18n.locale
    },
  },

  created() {
    this.get_a()
  },

  methods: {
    formatComma(value) {
      if (!value) return ''
      return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    actionVariant(actionId) {
      return ACTION_VARIANTS[actionId] || 'light-secondary'
    },

    async get_a() {
      const res = await axios.get('/property-action/v1/list')
      this.actions_a = res.data.data
    },

    async updateDate(property_id, avl_date, status_id, sale_price, rent_price, sqm, expire_date) {
      this.updateData({
        id: property_id,
        avaliable_date: avl_date,
        status_id,
        salePrice: sale_price,
        rentPrice: rent_price,
        salePriceSqm: sale_price && sqm ? sale_price / sqm : '',
        rentPriceSqm: rent_price && sqm ? rent_price / sqm : '',
        expire_date,
      })
    },

    condoLink(item) {
      const slug = (item.place_name['en'] || '').replace(/\s/g, '-')
      return `https://therealtors.net/condo/${slug}-${item.id}`
    },

    copyLink(item) {
      const link = this.condoLink(item)
      this.$copyText(link).then(
        () => this.showToast('Link copied'),
        () => this.showToast('Cannot copy', 'danger'),
      )
    },

    copyLinkNetwork(item) {
      const slug = (item.place_name['en'] || '').replace(/\s/g, '-')
      const link = `https://thepropology.com/condo/${slug}-${item.id}`
      this.$copyText(link).then(
        () => this.showToast('Link copied'),
        () => this.showToast('Cannot copy', 'danger'),
      )
    },

    doCopy(item) {
      let text = `${item.place_name.en} (${item.property_code})\n`
      text += `${item.bedroom} bedroom ${item.sqm} sq.m.`
      if (item.floors) text += ` ${item.floors} Fl.`
      text += `\n${item.furniture_name}\n`
      if (item.sale_price > 0) text += `Sale Price: ${this.formatComma(item.sale_price)} THB\n`
      if (item.rent_price > 0) text += `Rent Price: ${this.formatComma(item.rent_price)} THB\n`
      text += this.condoLink(item)
      this.$copyText(text).then(
        () => this.showToast('Text copied'),
        () => this.showToast('Cannot copy', 'danger'),
      )
    },

    showToast(title, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'success' ? 'CopyIcon' : 'AlertCircleIcon', variant },
      })
    },

    formcomment_show(id) {
      this.propety_id = id
      this.isSidebarcommentOpen = true
    },
    formcomment_close() {
      this.isSidebarcommentOpen = false
    },
    onSubmitClose() {
      this.isSidebarcommentOpen = false
      this.refetchData()
    },

    deleteconfirm(id) {
      this.$bvModal.msgBoxConfirm('Please confirm that you want to delete this property.', {
        title: 'Confirm Delete',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Delete',
        cancelTitle: 'Cancel',
        cancelVariant: 'outline-secondary',
        hideHeaderClose: false,
        centered: true,
      }).then(confirmed => {
        if (confirmed) this.deleteData(id)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options { flex-wrap: nowrap; }
  ::v-deep .vs__selected { width: 100px; }
}

.img_property {
  width: 120px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
}

.list-property {
  min-width: 180px;
}

.cursor-pointer {
  cursor: pointer;
}

.text-red {
  color: #ea5455 !important;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
